import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authLogin } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Flame, LogIn, ArrowLeft, Lock, User as UserIcon, Loader2 } from 'lucide-react';
import { BRAND } from '../data/mock';

export default function AdminLogin() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user?.is_admin) navigate('/admin/dashboard', { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await authLogin(form.username.trim(), form.password);
      if (data.session_token) localStorage.setItem('zs_token', data.session_token);
      setUser(data.user);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    localStorage.setItem('zs_post_login', '/admin/dashboard');
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] ember-bg px-5">
      <div className="w-full max-w-md bg-neutral-950 border border-orange-500/25 rounded-3xl p-8 text-center shadow-2xl shadow-orange-500/10">
        <img src={BRAND.logo} alt="logo" className="w-24 h-24 mx-auto object-contain drop-shadow-[0_6px_18px_rgba(249,115,22,0.5)]" />
        <div className="inline-flex items-center gap-2 mt-5 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs uppercase tracking-[0.25em]">
          <Flame className="w-3.5 h-3.5" /> Owner Login
        </div>
        <h1 className="mt-5 font-display text-4xl tracking-wide">ZAYKA <span className="text-orange-500">SPHERE</span></h1>
        <p className="mt-2 text-white/60 text-sm">Sign in to manage your menu, content, orders and enquiries.</p>

        <form onSubmit={submit} className="mt-7 space-y-3 text-left">
          <div className="relative">
            <UserIcon className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="admin-username" placeholder="Username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="h-12 pl-10 bg-black/50 border-white/10" autoComplete="username" />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="admin-password" type="password" placeholder="Password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="h-12 pl-10 bg-black/50 border-white/10" autoComplete="current-password" />
          </div>
          {error && <p data-testid="admin-login-error" className="text-red-400 text-sm">{error}</p>}
          <Button data-testid="admin-login-submit" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4 mr-2" /> Sign In</>}
          </Button>
        </form>

        <div className="mt-5 flex items-center gap-3 text-white/30 text-xs uppercase tracking-widest">
          <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
        </div>
        <Button data-testid="admin-google-login" onClick={googleLogin} variant="outline" className="mt-4 w-full h-11 rounded-full border-white/15 bg-neutral-900 text-white/80 hover:text-orange-400 hover:border-orange-500/50 font-semibold uppercase tracking-widest text-xs">
          Continue with Google
        </Button>

        <button onClick={() => navigate('/')} className="mt-5 inline-flex items-center gap-1 text-white/50 hover:text-orange-400 text-xs uppercase tracking-widest">
          <ArrowLeft className="w-3 h-3" /> Back to site
        </button>
      </div>
    </div>
  );
}
